import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import instance, { apiEncryptRequest, decryptData } from "../../Api";
import { useAuth } from "../../AuthContext";
import toast from "react-hot-toast";
import AppToast from "../../AppToast";
import Loader from "../common/Loader";

const CYCLES = [
  { key: "monthly",     label: "Monthly",     months: 1  },
  { key: "quarterly",   label: "Quarterly",   months: 3  },
  { key: "half_yearly", label: "Half Yearly", months: 6  },
  { key: "annually",    label: "Annually",    months: 12 },
];

const CYCLE_PRICE_KEY = {
  monthly:     "price_monthly",
  quarterly:   "price_quarterly",
  half_yearly: "price_half_yearly",
  annually:    "price_annually",
};

const CYCLE_SAVINGS = {
  quarterly:   "Save ~5%",
  half_yearly: "Save ~10%",
  annually:    "Save ~15%",
};

const ALL_TAB = "__all__";

const AddonServicesPage = ({ isMobile }) => {
  const { smuser, appCurrency } = useAuth();
  const navigate = useNavigate();

  const [loading,       setLoading]       = useState(true);
  const [categories,    setCategories]    = useState([]);
  const [activeCategory,setActiveCategory]= useState(ALL_TAB);
  const [selectedCycle, setSelectedCycle] = useState("monthly");
  const [buyModal,      setBuyModal]      = useState(null);
  const [modalCycle,    setModalCycle]    = useState("monthly");
  const [couponCode,    setCouponCode]    = useState("");
  const [couponState,   setCouponState]   = useState(null);
  const [buying,        setBuying]        = useState(false);
  const [walletBal,     setWalletBal]     = useState(0);

  const showToast = useCallback(
    (msg) => toast((t) => <AppToast id={t.id} message={msg} isMobile={isMobile} />),
    [isMobile]
  );

  const fmt = (n) => {
    const sym = appCurrency?.symbol || "₹";
    return sym + parseFloat(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 });
  };

 const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const enc  = await apiEncryptRequest({ user_id: smuser?.id });
      const res  = await instance.post("/addon-services/list", enc);
       const data = await decryptData(res.data);
      console.log("[AddonServices] API response:", data);
      if (data?.success) {
        setCategories(data.categories || []);
      }
      setWalletBal(parseFloat(smuser?.total_credit ?? 0));
    } catch (e) {
      console.error("[AddonServices] fetch error:", e);
    }
    setLoading(false);
  },  [smuser?.id]); 

  useEffect(() => {
    setWalletBal(parseFloat(smuser?.total_credit ?? 0));
  }, [smuser?.wallet_balance]);
 
  useEffect(() => { fetchList(); }, [fetchList]);

  // Only categories that actually have services, for the tab list
  const nonEmptyCategories = useMemo(
    () => categories.filter((c) => c.services?.length > 0),
    [categories]
  );

  // Reset to "All" if the currently active category disappears (e.g. after refetch)
  useEffect(() => {
    if (activeCategory === ALL_TAB) return;
    const stillExists = nonEmptyCategories.some((c) => String(c.id) === String(activeCategory));
    if (!stillExists) setActiveCategory(ALL_TAB);
  }, [nonEmptyCategories, activeCategory]);

  const visibleCategories = useMemo(() => {
    if (activeCategory === ALL_TAB) return nonEmptyCategories;
    return nonEmptyCategories.filter((c) => String(c.id) === String(activeCategory));
  }, [nonEmptyCategories, activeCategory]);

  const totalServiceCount = useMemo(
    () => nonEmptyCategories.reduce((sum, c) => sum + (c.services?.length || 0), 0),
    [nonEmptyCategories]
  );

  const openModal = (svc) => {
    setBuyModal(svc);
    setModalCycle(selectedCycle);
    setCouponCode("");
    setCouponState(null);
  };
  const closeModal = () => { setBuyModal(null); setCouponCode(""); setCouponState(null); };

  const validateCoupon = async () => {
    if (!couponCode.trim() || !buyModal) return;
    const amount = parseFloat(buyModal[CYCLE_PRICE_KEY[modalCycle]] || 0);
    try {
      const enc  = await apiEncryptRequest({ user_id: smuser?.id, code: couponCode.trim().toUpperCase(), amount });
      const res  = await instance.post("/addon-services/validate-coupon", enc);
     const data = await decryptData(res.data);
      setCouponState(data);
      if (data?.valid) showToast(data.message);
    } catch { setCouponState({ valid: false, message: "Failed to validate coupon." }); }
  };

  const confirmPurchase = async () => {
    if (!buyModal || buying) return;
    setBuying(true);
    try {
      const enc  = await apiEncryptRequest({
        user_id: smuser?.id,
        addon_service_id: buyModal.id,
        billing_cycle: modalCycle,
        coupon_code: couponState?.valid ? couponCode.trim().toUpperCase() : "",
      });
      const res  = await instance.post("/addon-services/purchase", enc);
       const data = await decryptData(res.data);
      if (data?.success) {
        showToast(data.message || "Addon activated!");
        setWalletBal(parseFloat(data.new_balance ?? walletBal));
        closeModal();
        fetchList();
      } else {
        showToast(data?.message || "Purchase failed. Check wallet balance.");
      }
    } catch { showToast("Purchase failed. Please try again."); }
    setBuying(false);
  };

  const getPrice   = (svc, cycle) => parseFloat(svc[CYCLE_PRICE_KEY[cycle]] || 0);
  const modalPrice = buyModal ? getPrice(buyModal, modalCycle) : 0;
  const modalDisc  = couponState?.valid ? (couponState.discount || 0) : 0;
  const modalFinal = Math.max(0, modalPrice - modalDisc);

  return (
    <div style={{ padding: isMobile ? "9px 6px 18px 7px" : "80px 59px 64px 221px", minHeight: "100vh", fontFamily: "'Nunito',sans-serif" }}>
      <style>{`
        .loader-container { position: relative !important; }
        .as-cycle:hover{transform:translateY(-1px)}
        .as-card:hover{transform:translateY(-3px)}
        .as-buybtn:hover{background:#E97730!important;transform:translateY(-1px)}
        .as-coupin:focus{border-color:#E97730!important;outline:none}
        .as-cattab:hover{background:#07528B!important;color:#fff!important}
        .as-cattab-scroll::-webkit-scrollbar{height:0px}
        .as-card{box-shadow:0 1px 2px rgba(16,24,40,.04)}
        .as-card:hover{box-shadow:0 14px 30px rgba(16,24,40,.09)}
        @keyframes asSpin{to{transform:rotate(360deg)}}
        @keyframes asFade{from{opacity:0;transform:scale(.95)}to{opacity:1;transform:scale(1)}}
      `}</style>

      {/* Title bar */}
      <div style={{ background:"linear-gradient(135deg,#E97730,#e8821a)", borderRadius:16, padding:isMobile?"18px 20px":"22px 28px", marginBottom:22, display:"flex", alignItems:"center", justifyContent:"space-between", flexWrap:"wrap", gap:12 }}>
        <div>
          <div style={{ fontSize:isMobile?20:24, fontWeight:800, color:"#fff" }}>🧩 Add-on Services</div>
          <div style={{ fontSize:13, color:"rgba(255,255,255,0.8)", marginTop:4, fontWeight:600 }}>Enhance your infrastructure with premium add-ons</div>
        </div>
        <div style={{ display:"flex", alignItems:"center", gap:10, flexWrap:"wrap" }}>
          <button onClick={() => navigate("/my-addons")} style={{ background:"#07528B", border:"none", borderRadius:10, padding:"9px 16px", fontSize:13, fontWeight:800, color:"#fff", cursor:"pointer", display:"flex", alignItems:"center", gap:6 }}>
            My Purchases
          </button>
        </div>
      </div>

      {/* Cycle selector */}
      <div style={{ background:"#fff", borderRadius:14, padding:"16px 20px", marginBottom:22, display:"flex", alignItems:"center", gap:10, flexWrap:"wrap" }}>
        <span style={{ fontSize:13, fontWeight:800, color:"#666", marginRight:4 }}>Billing Cycle:</span>
        {CYCLES.map((c) => (
          <button key={c.key} className="as-cycle" onClick={() => setSelectedCycle(c.key)}
            style={{ position:"relative", padding:"8px 18px", background:selectedCycle===c.key?"linear-gradient(135deg,#E97730,#e8821a)":"#f5f7fa", color:selectedCycle===c.key?"#fff":"#555", border:`1.5px solid ${selectedCycle===c.key?"#E97730":"#dde3ec"}`, borderRadius:10, fontSize:13, fontWeight:700, cursor:"pointer", transition:"all .2s" }}>
            {c.label}
          </button>
        ))}
      </div>

      {/* Category tabs (mobile: horizontal scroll strip on top) */}
      {!loading && nonEmptyCategories.length > 0 && isMobile && (
        <div className="as-cattab-scroll" style={{ display:"flex", gap:8, overflowX:"auto", paddingBottom:4, marginBottom:18, WebkitOverflowScrolling:"touch" }}>
          <button className="as-cattab" onClick={() => setActiveCategory(ALL_TAB)}
            style={{ flexShrink:0, display:"flex", alignItems:"center", gap:6, padding:"9px 16px", borderRadius:10, fontSize:13, fontWeight:800, whiteSpace:"nowrap", cursor:"pointer", transition:"all .15s",
              background:activeCategory===ALL_TAB?"linear-gradient(135deg,#E97730,#e8821a)":"#fff",
              color:activeCategory===ALL_TAB?"#fff":"#555",
              border:`1.5px solid ${activeCategory===ALL_TAB?"#E97730":"#e8edf5"}` }}>
            <span>🗂️</span>All <span style={{ opacity:.75 }}>({totalServiceCount})</span>
          </button>
          {nonEmptyCategories.map((cat) => (
            <button key={cat.id} className="as-cattab" onClick={() => setActiveCategory(cat.id)}
              style={{ flexShrink:0, display:"flex", alignItems:"center", gap:6, padding:"9px 16px", borderRadius:10, fontSize:13, fontWeight:800, whiteSpace:"nowrap", cursor:"pointer", transition:"all .15s",
                background:String(activeCategory)===String(cat.id)?"linear-gradient(135deg,#E97730,#e8821a)":"#fff",
                color:String(activeCategory)===String(cat.id)?"#fff":"#555",
                border:`1.5px solid ${String(activeCategory)===String(cat.id)?"#E97730":"#e8edf5"}` }}>
              <span>{cat.icon || "⚡"}</span>{cat.name} <span style={{ opacity:.75 }}>({cat.services.length})</span>
            </button>
          ))}
        </div>
      )}

      {/* Body: left category sidebar (desktop) + right content */}
      {loading ? (
        <div style={{ textAlign:"center", padding:"80px 20px" }}>
          <Loader isLoading={loading} />
        </div>
      ) : nonEmptyCategories.length === 0 ? (
        <div style={{ textAlign:"center", padding:"80px 20px", background:"#fff", borderRadius:16}}>
          <div style={{ fontSize:56, marginBottom:14 }}>🧩</div>
          <div style={{ fontSize:18, fontWeight:800, color:"#333", marginBottom:6 }}>No services available yet</div>
          <div style={{ fontSize:14, color:"#888", fontWeight:600 }}>Your admin is setting things up. Check back soon!</div>
        </div>
      ) : (
        <div style={{ display:"flex", alignItems:"flex-start", gap:24 }}>

          {/* Left sidebar tabs - desktop only */}
          {!isMobile && (
            <div style={{ width:230, flexShrink:0, background:"#fff", borderRadius:14, padding:10, position:"sticky", top:20 }}>
              <button className="as-cattab" onClick={() => setActiveCategory(ALL_TAB)}
                style={{ width:"100%", display:"flex", alignItems:"center", gap:10, padding:"11px 14px", borderRadius:10, fontSize:13.5, fontWeight:800, cursor:"pointer", transition:"all .15s", marginBottom:4, textAlign:"left",
                  background:activeCategory===ALL_TAB?"linear-gradient(135deg,#E97730,#e8821a)":"transparent",
                  color:activeCategory===ALL_TAB?"#fff":"#444",
                  border:"none" }}>
                <span style={{ fontSize:17 }}>🗂️</span>
                <span style={{ flex:1 }}>All Services</span>
                <span style={{ fontSize:11, fontWeight:800, opacity:.8 }}>{totalServiceCount}</span>
              </button>

              {nonEmptyCategories.map((cat) => {
                const isActive = String(activeCategory) === String(cat.id);
                return (
                  <button key={cat.id} className="as-cattab" onClick={() => setActiveCategory(cat.id)}
                    style={{ width:"100%", display:"flex", alignItems:"center", gap:10, padding:"11px 14px", borderRadius:10, fontSize:13.5, fontWeight:800, cursor:"pointer", transition:"all .15s", marginBottom:4, textAlign:"left",
                      background:isActive?"linear-gradient(135deg,#E97730,#e8821a)":"transparent",
                      color:isActive?"#fff":"#444",
                      border:"none" }}>
                    <span style={{ fontSize:17 }}>{cat.icon || "⚡"}</span>
                    <span style={{ flex:1, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{cat.name}</span>
                    <span style={{ fontSize:11, fontWeight:800, opacity:.8 }}>{cat.services.length}</span>
                  </button>
                );
              })}
            </div>
          )}

          {/* Right content */}
          <div style={{ flex:1, minWidth:0 }}>
            {visibleCategories.map((cat) => (
              <div key={cat.id} style={{ marginBottom:32 }}>
                {/* Category header */}
                <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:18, paddingBottom:14, borderBottom:"1px solid #eef1f6" }}>
                  <div style={{ width:44, height:44, borderRadius:12, background:"#fff", border:"1px solid #eef1f6", boxShadow:"0 1px 2px rgba(16,24,40,.06)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:20}}>
                    {cat.icon || "⚡"}
                  </div>
                  <div>
                    <div style={{ fontSize:17, fontWeight:800, color:"#1a2332", letterSpacing:"-.01em" }}>{cat.name}</div>
                    {cat.description && <div style={{ fontSize:12.5, color:"#8a94a6", fontWeight:600, marginTop:1 }}>{cat.description}</div>}
                  </div>
                </div>

                {/* Cards */}
                <div style={{ display:"grid", gridTemplateColumns:isMobile?"1fr":"repeat(auto-fill,minmax(270px,1fr))", gap:18 }}>
                  {cat.services.map((svc) => (
                    <div key={svc.id} className="as-card" style={{ background:"#fff", border:"1px solid #eef1f6", borderRadius:18, overflow:"hidden", display:"flex", flexDirection:"column", transition:"box-shadow .2s, transform .2s" }}>
                      <div style={{ padding:"22px 22px 16px" }}>
                        <div style={{ fontSize:15.5, fontWeight:800, color:"#1a2332", marginBottom:6, letterSpacing:"-.01em" }}>{svc.name}</div>
                        {svc.description && <div style={{ fontSize:12.5, color:"#8a94a6", lineHeight:1.6, marginBottom:14 }}>{svc.description}</div>}
                        {svc.features?.length > 0 && (
                          <ul style={{ listStyle:"none", padding:0, margin:0, display:"flex", flexDirection:"column", gap:8 }}>
                            {svc.features.slice(0,4).map((f,i) => (
                              <li key={i} style={{ display:"flex", alignItems:"flex-start", gap:8, fontSize:12.5, fontWeight:600, color:"#4b5563" }}>
                                <span style={{ width:16, height:16, borderRadius:"50%", background:"#fff3e8", color:"#E97730", flexShrink:0, display:"flex", alignItems:"center", justifyContent:"center", fontSize:10, fontWeight:900, marginTop:1 }}>✓</span>{f}
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                      <div style={{ background:"#fbfbfd", borderTop:"1px solid #eef1f6", padding:"16px 22px", display:"flex", alignItems:"center", justifyContent:"space-between", marginTop:"auto" }}>
                        <div>
                          <div style={{ fontSize:22, fontWeight:800, color:"#1a2332", lineHeight:1, letterSpacing:"-.02em" }}>{fmt(getPrice(svc,selectedCycle))}</div>
                          <div style={{ fontSize:11, color:"#9aa3b2", fontWeight:700, marginTop:3 }}>per {CYCLES.find((c) => c.key===selectedCycle)?.label.toLowerCase()}</div>
                        </div>
                        <button className="as-buybtn" onClick={() => openModal(svc)} style={{ padding:"10px 20px", background:"#07528B", color:"#fff", border:"none", borderRadius:10, fontSize:13, fontWeight:800, cursor:"pointer", transition:"all .2s" }}>
                          Buy Now
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Buy Modal */}
      {buyModal && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.5)", backdropFilter:"blur(6px)", zIndex:9999, display:"flex", alignItems:"center", justifyContent:"center", padding:16 }} onClick={(e) => { if(e.target===e.currentTarget) closeModal(); }}>
          <div style={{ background:"#fff", borderRadius:20, width:"100%", maxWidth:440, animation:"asFade .2s ease", overflow:"hidden" }}>
            {/* Modal header */}
            <div style={{ background:"#07528B", padding:"20px 24px" }}>
              <div style={{ fontSize:18, fontWeight:800, color:"#fff" }}>{buyModal.name}</div>
              {buyModal.description && <div style={{ fontSize:12, color:"rgba(255,255,255,0.8)", marginTop:4 }}>{buyModal.description}</div>}
            </div>

            <div style={{ padding:24 }}>
              {/* Cycle */}
              <div style={{ fontSize:11, fontWeight:800, textTransform:"uppercase", letterSpacing:".1em", color:"#aaa", marginBottom:8 }}>Billing Cycle</div>
              <div style={{ display:"flex", gap:6, flexWrap:"wrap", marginBottom:18 }}>
                {CYCLES.map((c) => (
                  <button key={c.key} onClick={() => { setModalCycle(c.key); setCouponState(null); }}
                    style={{ padding:"7px 14px", background:modalCycle===c.key?"#07528B":"#f5f7fa", color:modalCycle===c.key?"#fff":"#555", border:`1px solid ${modalCycle===c.key?"#07528B":"#dde3ec"}`, borderRadius:8, fontSize:12, fontWeight:700, cursor:"pointer" }}>
                    {c.label}
                    {CYCLE_SAVINGS[c.key] && <span style={{ fontSize:9, marginLeft:4, color:modalCycle===c.key?"rgba(255,255,255,0.85)":"#16a34a", fontWeight:800 }}>{CYCLE_SAVINGS[c.key]}</span>}
                  </button>
                ))}
              </div>

              {/* Coupon */}
              <div style={{ fontSize:11, fontWeight:800, textTransform:"uppercase", letterSpacing:".1em", color:"#aaa", marginBottom:8 }}>Coupon Code (optional)</div>
              <div style={{ display:"flex", gap:8, marginBottom:6 }}>
                <input type="text" className="as-coupin" placeholder="ENTER CODE" value={couponCode}
                  onChange={(e) => { setCouponCode(e.target.value.toUpperCase()); setCouponState(null); }}
                  style={{ flex:1, background:"#f5f7fa", border:"1.5px solid #dde3ec", borderRadius:8, padding:"10px 13px", fontSize:13, fontWeight:700, color:"#333", fontFamily:"inherit", textTransform:"uppercase", letterSpacing:".08em" }} />
                <button onClick={validateCoupon} style={{ padding:"10px 16px", background:"rgba(247,160,34,0.1)", color:"#07528B", border:"1px solid rgba(247,160,34,0.3)", borderRadius:8, fontSize:13, fontWeight:800, cursor:"pointer", whiteSpace:"nowrap" }}>Apply</button>
              </div>
              {couponState && <div style={{ fontSize:12, fontWeight:700, marginBottom:12, color:couponState.valid?"#16a34a":"#dc2626" }}>{couponState.valid?"✓ ":"✕ "}{couponState.message}</div>}

              {/* Summary */}
              <div style={{ background:"#f5f7fa", border:"1px solid #dde3ec", borderRadius:12, padding:"14px 16px", marginBottom:16 }}>
                <div style={{ display:"flex", justifyContent:"space-between", padding:"5px 0", fontSize:14, fontWeight:600, color:"#333", borderBottom:"1px solid #e8edf5" }}>
                  <span>Price ({CYCLES.find((c) => c.key===modalCycle)?.label})</span><span>{fmt(modalPrice)}</span>
                </div>
                {modalDisc > 0 && (
                  <div style={{ display:"flex", justifyContent:"space-between", padding:"5px 0", fontSize:14, fontWeight:600, color:"#16a34a", borderBottom:"1px solid #e8edf5" }}>
                    <span>Coupon Discount</span><span>-{fmt(modalDisc)}</span>
                  </div>
                )}
                <div style={{ display:"flex", justifyContent:"space-between", padding:"5px 0", fontSize:14, fontWeight:600, borderBottom:"1px solid #e8edf5", color:walletBal>=modalFinal?"#16a34a":"#dc2626" }}>
                  <span>Your Wallet</span><span>{fmt(walletBal)}</span>
                </div>
                <div style={{ display:"flex", justifyContent:"space-between", paddingTop:10, marginTop:4, fontSize:18, fontWeight:800, color:"#1a2332" }}>
                  <span>Total Payable</span><span style={{ color:"#E97730" }}>{fmt(modalFinal)}</span>
                </div>
              </div>

              {/* Insufficient warning */}
              {walletBal < modalFinal && (
                <div style={{ background:"rgba(220,38,38,0.05)", border:"1px solid rgba(220,38,38,0.2)", borderRadius:8, padding:"10px 14px", fontSize:13, fontWeight:600, color:"#dc2626", marginBottom:14 }}>
                  ⚠ Insufficient wallet balance. You need {fmt(modalFinal - walletBal)} more.
                </div>
              )}

              {/* Buttons */}
              <div style={{ display:"flex", gap:10 }}>
                <button onClick={closeModal} style={{ padding:"12px 20px", background:"#f5f7fa", border:"1.5px solid #dde3ec", borderRadius:10, fontSize:14, fontWeight:700, color:"#888", cursor:"pointer" }}>Cancel</button>
                <button onClick={confirmPurchase} disabled={buying||walletBal<modalFinal}
                  style={{ flex:1, padding:12, background:buying||walletBal<modalFinal?"#ccc":"linear-gradient(135deg,#E97730,#e8821a)", color:"#fff", border:"none", borderRadius:10, fontSize:14, fontWeight:800, cursor:buying||walletBal<modalFinal?"not-allowed":"pointer", transition:"all .2s" }}>
                  {buying ? "Processing..." : `✓ Buy for ${fmt(modalFinal)}`}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddonServicesPage;